import React from 'react';

interface ProgressStep {
  id: string;
  title: string;
  description: string;
  icon?: React.ReactNode;
}

interface ProgressIndicatorProps {
  steps: ProgressStep[];
  currentStep: number;
  orientation?: 'horizontal' | 'vertical';
  showDescription?: boolean;
  compact?: boolean;
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  steps,
  currentStep,
  orientation = 'horizontal',
  showDescription = true,
  compact = false
}) => {
  const getStepStatus = (index: number) => {
    if (index < currentStep) return 'completed';
    if (index === currentStep) return 'active';
    return 'pending';
  };

  const defaultIcons = {
    completed: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
    ),
    active: (
      <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
    ),
    pending: (
      <div className="w-2 h-2 bg-current rounded-full opacity-40" />
    )
  };

  if (compact) {
    return (
      <div className="flex items-center space-x-2">
        {/* Compact Progress Bar */}
        <div className="flex-1 bg-neutral-200 rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-500 ease-out"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
        <span className="text-sm font-medium text-neutral-700">
          {currentStep + 1} / {steps.length}
        </span>
      </div>
    );
  }

  return (
    <div className={`${orientation === 'vertical' ? 'space-y-4' : ''}`}>
      <div className={`flex ${orientation === 'vertical' ? 'flex-col' : 'items-center justify-between'}`}>
        {steps.map((step, index) => {
          const status = getStepStatus(index);
          const isLast = index === steps.length - 1;

          return (
            <div
              key={step.id}
              className={`${orientation === 'horizontal' ? 'flex-1' : 'flex'} ${
                !isLast && orientation === 'horizontal' ? 'relative' : ''
              }`}
            >
              {/* Step */}
              <div className={`flex ${orientation === 'vertical' ? 'flex-row' : 'flex-col'} items-center`}>
                {/* Circle/Icon */}
                <div className="relative">
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
                      ${status === 'completed' 
                        ? 'bg-success-600 text-white shadow-soft-md' 
                        : status === 'active' 
                        ? 'bg-primary-600 text-white shadow-glow-md animate-pulse-soft' 
                        : 'bg-neutral-200 text-neutral-400'
                      }
                    `}
                  >
                    {step.icon ? step.icon : defaultIcons[status]}
                  </div>
                  
                  {/* Pulse effect for active step */}
                  {status === 'active' && (
                    <div className="absolute inset-0 rounded-full bg-primary-600 animate-ping opacity-20" />
                  )}
                </div>

                {/* Text */}
                <div className={`${orientation === 'vertical' ? 'ml-4 flex-1' : 'mt-3 text-center'}`}>
                  <div
                    className={`font-semibold text-sm transition-colors duration-300 ${
                      status === 'completed'
                        ? 'text-success-700'
                        : status === 'active'
                        ? 'text-primary-700'
                        : 'text-neutral-500'
                    }`}
                  >
                    {step.title}
                  </div>
                  {showDescription && (
                    <div
                      className={`text-xs mt-1 transition-colors duration-300 ${
                        status === 'completed'
                          ? 'text-success-600'
                          : status === 'active'
                          ? 'text-primary-600'
                          : 'text-neutral-400'
                      }`}
                    >
                      {step.description}
                    </div>
                  )}
                </div>
              </div>

              {/* Connector Line */}
              {!isLast && (
                <div
                  className={`
                    ${orientation === 'horizontal' 
                      ? 'absolute top-5 left-[calc(50%+20px)] right-[calc(-50%+20px)] h-0.5' 
                      : 'ml-5 mt-2 mb-2 w-0.5 h-8'
                    }
                    ${status === 'completed' 
                      ? 'bg-success-500' 
                      : 'bg-neutral-300'
                    }
                    transition-colors duration-300
                  `}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgressIndicator;